/**
 * What ships beside the number, ported from `src/interpret_score.py` and `src/detect.py`.
 *
 * **The owner closed this wording on 2026-08-17.** Item 30 decided what the detector says, and
 * item 31 was told to print that wording rather than write its own. The same rule holds here.
 *
 * This file therefore adds no sentence of its own. It reads `user_caveat` and `score_bins` out of
 * `detector.json`, which `web/tools/export_model.py` copies straight from the Python source. A
 * change to the wording happens in `interpret_score.py`, and the export carries it here.
 *
 * The one thing this file does add is a line break. The terminal tool prints fixed-width lines,
 * and a browser reflows text to the window. `caveatBullets` joins the wrapped lines back into
 * whole sentences, so the words survive and only the wrapping changes.
 * `test/interpret.test.ts` checks that no word is lost.
 */

import type { DetectorMeta, ScoreBin } from './types';

export class InterpretationError extends Error {}

/** How the tool refuses a text, or notes that it is short. */
export interface LengthVerdict {
  /** `true` when the detector will not score the text at all. */
  refused: boolean;
  /** The message to show, or `null` when the length needs no comment. */
  message: string | null;
}

/** Group the digits of a whole number the way the Python format `:,` does. */
function grouped(value: number): string {
  return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

/**
 * Return the bin that holds one score.
 *
 * The rule matches `row_of`: a bin holds its low edge and not its high edge, and the last bin also
 * holds every score at or above its high edge. A score of exactly 1.00 therefore lands in the top
 * bin rather than falling off the table.
 */
export function binOf(score: number, bins: ScoreBin[]): ScoreBin {
  for (const bin of bins) {
    const last = bin === bins[bins.length - 1];
    if ((score >= bin.low && score < bin.high) || (last && score >= bin.high)) {
      return bin;
    }
  }
  throw new InterpretationError(`the bins cover no score of ${score}. The table is not complete`);
}

/**
 * Return the sentence that says what the score meant on the test data.
 *
 * An empty bin carries no share, so it carries no sentence. The shipped table fills every bin, and
 * the branch covers a table built from less data.
 */
export function meaning(score: number, meta: DetectorMeta): string {
  const bin = binOf(score, meta.score_bins);

  if (bin.count === 0 || bin.ai_share === null) {
    return (
      `No test passage scored between ${bin.low.toFixed(2)} and ${bin.high.toFixed(2)}, ` +
      'so this tool cannot say what that score meant.'
    );
  }

  const share = Math.round(bin.ai_share * 100);
  return (
    `Of the ${grouped(bin.count)} test passages that scored between ` +
    `${bin.low.toFixed(2)} and ${bin.high.toFixed(2)}, ${share} out of 100 were written by ` +
    `${meta.detected_model}, an AI writing tool.`
  );
}

/**
 * Return the caveat as whole sentences.
 *
 * `user_caveat` arrives as terminal lines: a heading, a blank line, and bullets that wrap. The
 * function joins each bullet back together and drops the blank lines. It changes no word.
 */
export function caveatBullets(meta: DetectorMeta): { heading: string; bullets: string[] } {
  const lines = meta.user_caveat.filter((line) => line.trim() !== '');
  const heading = lines[0] ?? '';
  const bullets: string[] = [];

  for (const line of lines.slice(1)) {
    const trimmed = line.trim();
    if (trimmed.startsWith('- ')) {
      bullets.push(trimmed.slice(2));
    } else if (bullets.length > 0) {
      bullets[bullets.length - 1] += ` ${trimmed}`;
    }
  }
  return { heading, bullets };
}

/**
 * Judge the length of a text against the training range.
 *
 * The rule has two steps, and it matches `check_length`:
 *
 * 1. Below the floor the detector refuses. It read nothing of that length, and a number there
 *    would be confident and unfounded.
 * 2. Between the floor and the shortest training passage the score stands, with a note.
 */
export function judgeLength(letters: number, meta: DetectorMeta): LengthVerdict {
  const { min_letters: low, max_letters: high, refuse_below_letters: floor } = meta.length;

  if (letters < floor) {
    return {
      refused: true,
      message:
        `The text holds ${grouped(letters)} letters, and the detector will not score it. ` +
        `The training passages hold ${grouped(low)} to ${grouped(high)} letters, and this tool ` +
        `refuses a text below ${grouped(floor)}. ` +
        'A shorter text gets a confident number with nothing behind it.',
    };
  }

  if (letters < low) {
    return {
      refused: false,
      message:
        `Note: this text holds ${grouped(letters)} letters. ` +
        `The shortest passage that the tool trained on held ${grouped(low)}.`,
    };
  }

  return { refused: false, message: null };
}
